---
title: Control the chat widget from your page
summary: Open and close the widget from your own button, react to what the visitor does, and set the launcher's size and position.
visibility: authenticated
feature: widget
audience: author-admin
section: How-to
order: 67
last_verified: 2026-09-22
owner: docs
---

The embed publishes a small object on your page, `window.nrWidget`. Use it to
open the chat from your own button, to know when a visitor starts a
conversation, or to take navigation over on a single-page app.

It is deliberately small, and it stays small. It never exposes conversation
text, the visitor's id, the session token, or anything inside the widget.

## Opening and closing

```js
nrWidget.open()
nrWidget.close()
nrWidget.toggle()
nrWidget.isOpen() // true while the panel is on screen
```

`isOpen()` reports what the visitor can see. If you call `open()` while the
widget is still starting up, the panel appears as soon as it is ready and
`isOpen()` becomes true at that moment.

## Reacting to what happens

`on(event, handler)` returns a function that stops listening.

```js
const off = nrWidget.on('open', () => {
  console.log('the visitor opened the chat')
})
off()
```

| Event | When | What your handler receives |
| --- | --- | --- |
| `ready` | the widget has loaded and is usable | nothing |
| `open` | the panel appeared | nothing |
| `close` | the panel closed | nothing |
| `message` | a message was sent or received | `{role, id}` |
| `navigate` | the widget is about to open a link | `{url, preventDefault(), defaultPrevented}` |

`ready` is remembered: if you subscribe after the widget has already loaded,
your handler still runs, on the next tick.

`message` carries the role — `'user'` or `'assistant'` — and an id, never the
text. The visitor's own messages carry an id that is local to that page load;
the assistant's is the stored message id.

Subscribe to `open` and `close` as soon as your script runs, not from inside
a `ready` handler — an `open()` call, or a visitor clicking the launcher,
can happen before the widget finishes starting up, and that `open` event
would fire before `ready` does.

If one of your handlers throws, the widget logs it and carries on. Your other
handlers still run.

## Taking navigation over

When the widget opens a link — a source, or a page it is pointing the visitor
at — it tells you first. Cancel it and the destination is yours:

```js
nrWidget.on('navigate', event => {
  event.preventDefault()
  myRouter.push(event.url)
})
```

Returning `false` from the handler does the same thing. Do nothing, and the
widget opens the link itself. `event.url` is the address the widget would
really open, after any site rules have been applied.

`navigate` only fires for `http(s)` links. A `mailto:` or `tel:` link opens
however the visitor's browser normally handles it, without telling your
handler first.

## Calling it before the widget has loaded

The embed script is `async`, so your own code may run first. Declare this
one-liner above your other scripts and anything you queue is replayed the
moment the widget starts:

```html
<script>
window.nrWidget=window.nrWidget||{q:[],open(){this.q.push(['open'])},
  close(){this.q.push(['close'])},toggle(){this.q.push(['toggle'])},
  isOpen(){return false},
  on(e,f){const c=['on',e,f];this.q.push(c);return()=>{c.length=0}}};
</script>
```

You only need this if your code runs before the embed script. Anything that
runs after it can use `window.nrWidget` directly.

Copy this snippet as-is rather than writing your own version of it — the
widget relies on this exact `off()` (it truncates its own queued entry) to
tell a cancelled subscription apart from a live one.

## Sizing and moving the launcher

The launcher — the chat bubble — is configured per widget, in **Admin →
Widgets → Branding**. Nothing goes in your page's HTML, so you can change
it without touching your site.

| Setting | Default | Range |
| --- | --- | --- |
| Launcher size | 56 px | 32–96 |
| Horizontal offset | 20 px | 0–200 |
| Vertical offset | 20 px | 0–200 |

Raise the vertical offset to lift the bubble clear of a cookie banner, a
"back to top" button or a fixed footer. The chat panel follows the launcher,
so you only set this once. On phones with a home indicator, the launcher
clears it automatically; the panel doesn't need to — on a phone it opens
full-screen.

**Hide the launcher** draws no bubble at all. Use it when you want to open the
chat from your own button — which is the most complete way to make the widget
match your site:

```html
<button onclick="nrWidget.open()">Chat with us</button>
```
